import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { createNotification } from '../../services/NotificationSystem';
import { Globe, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import PageHeader from '../common/PageHeader';

// 관리자 — 해외 기공소 가입 심사 (기획서 §1.1: pending_review → active/rejected)
const STATUS_LABEL = {
    pending_review: { text: '심사 대기', bg: '#FDF3E3', color: '#B45309' },
    active: { text: '활성', bg: '#E6F5F1', color: '#0B6E5D' },
    rejected: { text: '보류', bg: '#FEF2F2', color: '#DC2626' },
};

function AdminGlobalLabs() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending_review');
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, 'users'), where('labKind', '==', 'overseas')));
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setRows(list);
        } catch (e) {
            console.error('해외 기공소 목록 로드 실패:', e);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (row, status) => {
        const labels = { active: '승인', rejected: '보류', pending_review: '심사 대기로 되돌리기' };
        if (!window.confirm(`${row.businessName || row.email} — ${labels[status]} 처리할까요?`)) return;
        setBusyId(row.id);
        try {
            // 🔐 가입 승인제 통일 필드(approvalStatus)도 함께 갱신 (2026-08-25) — 웹 공통 승인 게이트와 데이터 일관성 유지
            const approvalMap = { active: 'approved', rejected: 'rejected', pending_review: 'pending' };
            await updateDoc(doc(db, 'users', row.id), {
                globalStatus: status,
                globalReviewedAt: new Date(),
                approvalStatus: approvalMap[status] || 'pending',
                approvedAt: new Date(),
                approvedBy: auth.currentUser?.uid || 'admin',
            });
            // 심사 결과를 해당 해외 사용자 웹 알림함에 영문으로 통지 — 실패해도 상태 변경은 유지
            const notice = status === 'active'
                ? { title: 'Account approved', message: 'Your DentCluster Global account has been approved. You can now sign in and start sending cases.' }
                : status === 'rejected'
                    ? { title: 'Application on hold', message: 'Your DentCluster Global application is on hold. Please contact us for details.' }
                    : null;   // pending_review로 되돌리기는 알림 없음
            if (notice) {
                try {
                    await createNotification({
                        recipientId: row.id,
                        type: 'global_account_review',
                        title: notice.title,
                        message: notice.message,
                        link: '/global/status',
                        metadata: { globalStatus: status },
                    });
                } catch (e) { /* 알림 실패는 무시 */ }
            }
            setRows(prev => prev.map(r => r.id === row.id ? { ...r, globalStatus: status } : r));
        } catch (e) {
            console.error('상태 변경 실패:', e);
            alert('상태 변경에 실패했습니다.');
        } finally {
            setBusyId(null);
        }
    };

    const visible = rows.filter(r => filter === 'all' || (r.globalStatus || 'pending_review') === filter);
    const fmtDate = (v) => v?.toDate ? v.toDate().toLocaleDateString('ko-KR') : '-';

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: 20 }}>
            <PageHeader icon={Globe} title="해외 기공소 심사" />
            <div style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
                {['pending_review', 'active', 'rejected', 'all'].map(f => (
                    <button key={f} onClick={() => setFilter(f)}
                        style={{
                            padding: '7px 14px', borderRadius: 8, border: '1px solid #E4E6EE', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                            background: filter === f ? '#4F52C7' : '#fff', color: filter === f ? '#fff' : '#5A6072',
                        }}>
                        {f === 'all' ? '전체' : STATUS_LABEL[f].text} ({rows.filter(r => f === 'all' || (r.globalStatus || 'pending_review') === f).length})
                    </button>
                ))}
            </div>

            {loading ? <div style={{ color: '#5A6072', padding: 30 }}>불러오는 중…</div> : (
                <div style={{ background: '#fff', border: '1px solid #E4E6EE', borderRadius: 12, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                        <thead>
                            <tr style={{ background: '#F8FAFC' }}>
                                {['업체명', '국가', '통화', '담당자', '이메일', '신청일', '상태', '처리'].map(h => (
                                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12, color: '#64748B', borderBottom: '1px solid #E4E6EE' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visible.length === 0 && (
                                <tr><td colSpan={8} style={{ padding: 26, textAlign: 'center', color: '#94A3B8' }}>해당 상태의 신청이 없습니다.</td></tr>
                            )}
                            {visible.map(r => {
                                const st = STATUS_LABEL[r.globalStatus || 'pending_review'];
                                return (
                                    <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                        <td style={{ padding: '10px 14px', fontWeight: 700 }}>{r.businessName || '-'}</td>
                                        <td style={{ padding: '10px 14px' }}>{r.country || '-'}</td>
                                        <td style={{ padding: '10px 14px' }}>{r.currency || '-'}</td>
                                        <td style={{ padding: '10px 14px' }}>{r.representativeName || r.name || '-'}</td>
                                        <td style={{ padding: '10px 14px', color: '#5A6072' }}>{r.email || '-'}</td>
                                        <td style={{ padding: '10px 14px', color: '#5A6072' }}>{fmtDate(r.createdAt)}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: st.bg, color: st.color }}>{st.text}</span>
                                        </td>
                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                            {(r.globalStatus || 'pending_review') === 'pending_review' ? (
                                                <>
                                                    <button disabled={busyId === r.id} onClick={() => setStatus(r, 'active')} style={btn('#0E8A74')}><CheckCircle size={13} /> 승인</button>
                                                    <button disabled={busyId === r.id} onClick={() => setStatus(r, 'rejected')} style={btn('#DC2626')}><XCircle size={13} /> 보류</button>
                                                </>
                                            ) : (
                                                <button disabled={busyId === r.id} onClick={() => setStatus(r, 'pending_review')} style={btn('#64748B')}><RotateCcw size={13} /> 심사대기로</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const btn = (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 6,
    padding: '6px 10px', borderRadius: 7, border: `1px solid ${color}`, background: '#fff',
    color, fontSize: 12, fontWeight: 700, cursor: 'pointer',
});

export default AdminGlobalLabs;
